import { Injectable, Inject, OnDestroy, PLATFORM_ID } from "@angular/core";
import { ReplaySubject, Observable, Subscription } from "rxjs";

import * as Rox from "rox-browser";
import { IpcFeatureToggleConfiguration } from "./feature-toggle.configuration";
import { CustomProperty } from "./feature-toggle.types";
import { isPlatformBrowser } from "@angular/common";

const isObservable = (obj: any | Observable<any>): obj is Observable<any> => {
    return !!obj && typeof obj.subscribe === "function";
};

const getOverridePosition = (position?: number): Rox.RoxOverridesPosition => {
    switch (position) {
        case 1:
            return Rox.RoxOverridesPosition.TopRight;
        case 2:
            return Rox.RoxOverridesPosition.BottomRight;
        case 3:
            return Rox.RoxOverridesPosition.BottomLeft;
        case 4:
            return Rox.RoxOverridesPosition.TopLeft;
        default:
            return;
    }
};

@Injectable()
export class IpcFeatureToggleService implements OnDestroy {
    /**
     * @description Just in case if a re-render or something must be done after a property is changed, subscribing to this could be useful
     */
    public customPropertiesChanged: ReplaySubject<Map<string, CustomProperty>>;

    public features: Map<string, any> = new Map<string, any>();
    private customProperties: Map<string, CustomProperty> = new Map();
    private subscriptions: Array<Subscription> = [];

    constructor(@Inject("ROLLOUT_CONFIGURATION") private configuration: IpcFeatureToggleConfiguration, @Inject(PLATFORM_ID) private platformId: Object) {
        this.customPropertiesChanged = new ReplaySubject();

        if (configuration.customProperties && configuration.customProperties.size > 0) {
            configuration.customProperties.forEach((value, key) => {
                if (isObservable(value)) {
                    this.subscriptions.push(
                        value.subscribe(($) => {
                            this.setCustomProperty(key, $);
                        })
                    );
                } else {
                    this.setCustomProperty(key, value);
                }
            });
        }

        if (isPlatformBrowser(platformId)) {
            if (["Quality", "Development"].some((env) => env == configuration.environment)) {
                Object.defineProperty(window, "DEBUG__FEATURES__OVERRIDE", {
                    value: (position?: number) => {
                        if (!position) {
                            // tslint:disable-next-line: no-console tslint:disable-next-line: max-line-length
                            console.info(`Pass number 1 to 4 as the second parameter to sey the panel position clockwise, 1 being positioned 'top right' and 4 'top left'`);
                        }
                        Rox.showOverrides(getOverridePosition(position));
                    },
                    writable: false,
                    enumerable: false,
                    configurable: false,
                });

                Object.defineProperty(window, "DEBUG__FEATURES__CLEAR_OVERRIDE", {
                    value: Rox.overrides.clearOverride,
                    writable: false,
                    enumerable: false,
                    configurable: false,
                });

                Object.defineProperty(window, "DEBUG__FEATURES__FETCH", {
                    value: Rox.fetch,
                    writable: false,
                    enumerable: false,
                    configurable: false,
                });

                Object.defineProperty(window, "DEBUG__FEATURES__SET_CUSTOM_PROPERTY", {
                    value: this.setCustomProperty,
                    writable: false,
                    enumerable: false,
                    configurable: false,
                });

                Object.defineProperty(window, "DEBUG__FEATURES__CUSTOM_PROPERTIES", {
                    value: this.customProperties,
                    writable: false,
                    enumerable: true,
                    configurable: false,
                });
            }
        }
    }

    /**
     * @description Set a custom property
     * @param property The name of the property
     * @param value Property's value. string | boolean | number
     */
    public setCustomProperty(property: string, value: CustomProperty): void {
        switch (typeof property) {
            case "string":
                Rox.unfreeze();
                Rox.setCustomStringProperty(property, value as string);
                break;
            case "boolean":
                Rox.unfreeze();
                Rox.setCustomBooleanProperty(property, value as boolean);
                break;
            case "number":
                Rox.unfreeze();
                Rox.setCustomNumberProperty(property, value as number);
                break;
            default:
                console.warn("Unexpected property type");
                return;
        }

        this.customProperties.set(property, value);
        this.customPropertiesChanged.next(this.customProperties);
    }

    /**
     * @description Loads the defined containers.
     * An array with references to the container's classes must be provided as configuration.
     * Defining the flags as properties in a class makes it possible to import them where the flag needs to be used
     * and enables the IDE to provide code completion
     */
    private loadContainers(): void {
        if (!this.configuration.containers || this.configuration.containers.length === 0) {
            console.error(`There's no rollout containers defined`);
            return;
        }

        // As I'm catching this Rollout error, I need to implement this validation myself
        const uniqueContainers = new Set(this.configuration.containers.map(container => container.key))
        if (uniqueContainers.size !== this.configuration.containers.length) {
            console.error(`Rollout containers namespaces must be unique`);
            return;
        }

        this.configuration.containers.forEach((container) => {
            //console.log("Registering Rollout Container ", container.key, container.value);
            try {
                //console.log("Registering", container.key, "Value", container.value);
                Rox.register(container.key, container.value);

                for (const featureFlag in container.value) {
                    if (container.value.hasOwnProperty(featureFlag)) {
                        const element = container.value[featureFlag];
                        this.features.set(`${container.key}.${featureFlag}`, element);
                    }
                }
            } catch (err) {
                // Rollout throws an error if a namespace is not unique
                // Catching this error that only shows up at development time after a hot reload of the app
                // Hot reloading makes Rollout to register duplicated namespaces
                console.warn("Error registering namespace", err);
            }
        });
    }

    /**
     * @description Call this function after providing the needed configuration
     */
    public setup(): Promise<any> {
        this.loadContainers();
        return Rox.setup(this.configuration.key, this.configuration.options || {});
    }

    ngOnDestroy(): void {
        this.subscriptions && this.subscriptions.forEach((s) => s.unsubscribe());
    }
}
